import { axiosInstance as axios } from '@/utils/axiosConfig';
import { downloadExportedFile } from '@/utils/export';
import { useViewQueryParamsContext } from '@/components/views/ViewQueryParamsContext';
import { usePageContext } from '@/context/PageContext';
import { useApplicationQuery } from './useApplicationQuery';

export type ExportViewRecordsFormat = 'csv' | 'json' | 'text';

type ExportViewRecordsParams = {
  viewKey: string;
  fileFormat: ExportViewRecordsFormat;
  fileName: string | undefined;
};

export function useExportViewRecords() {
  const { data: application } = useApplicationQuery();
  const { params } = useViewQueryParamsContext();

  const { activePage } = usePageContext();

  if (!application) throw new Error('Application not found');
  if (!activePage) throw new Error('Page not found');

  const pageKey = activePage.key;
  const appId = application.id;

  async function exportViewRecords({
    viewKey,
    fileFormat,
    fileName = 'exported-table'
  }: ExportViewRecordsParams) {
    // We should pass the params of the records call.
    // This is done to export the records that are available in the view.
    const urlParams = new URLSearchParams();

    const { sortField, sortOrder, page, rowsPerPage, filters, search } = params;

    const queryParams = {
      ...(sortField &&
        sortOrder && {
          sort_field: sortField,
          sort_order: sortOrder
        }),
      ...(page && { page: page.toString() }),
      ...(rowsPerPage && { rows_per_page: rowsPerPage.toString() }),
      ...(filters && { filters: JSON.stringify(filters) }),
      ...(search && { search }),
      type: fileFormat
    };

    Object.entries(queryParams).forEach(([key, value]) => {
      if (value !== undefined) {
        urlParams.append(key, value);
      }
    });

    const url = `/v1/scenes/${pageKey}/views/${viewKey}/records/export/applications/${appId}/`;
    const { data: exportFile } = await axios.get<Blob>(url, {
      params: urlParams,
      withCredentials: true,
      responseType: 'blob'
    });
    downloadExportedFile(fileFormat, exportFile, fileName);
  }

  return exportViewRecords;
}
